import React, { useState, useEffect } from "react";
import { FaPlay, FaEye, FaSpinner, FaChevronLeft, FaChevronRight, FaTimesCircle, FaHourglassHalf, FaTicketAlt, FaTimes, FaMapMarkerAlt, FaCalendarAlt, FaBullseye, FaVideo, FaImage, FaLink, FaSearch, FaCopy } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link } from "react-router-dom";
import axios from "axios";

const RunningAdvertisements = () => {
  const [advertisements, setAdvertisements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showScreenshotModal, setShowScreenshotModal] = useState(false);
  const [currentScreenshot, setCurrentScreenshot] = useState(null);
  const [checkingViews, setCheckingViews] = useState({});
  const [adViews, setAdViews] = useState({});
  // Pagination state for proofs table (per ad)
  const [adPageMap, setAdPageMap] = useState({});
  const rowsPerPage = 50;

  // Pagination state for main advertisements list
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState(''); // State for Searching by ID
  const itemsPerPage = 50;

  useEffect(() => {
    const fetchRunningAds = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/get/running/ads`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        console.log("Running Ads Response:", response);

        if (response.status === 404) {
          setAdvertisements([]);
          return;
        }

        const data = response.data.data || response.data;
        const ads = Array.isArray(data) ? data : [data];
        setAdvertisements(ads);
        setCurrentPage(1); // Reset to first page when data changes
      } catch (error) {
        if (error.response && error.response.status === 404) {
          setAdvertisements([]);
        } else {
          toast.error("Failed to load advertisement details");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchRunningAds();
  }, []);

  const handleCheckCurrentView = async (
    platform,
    videoLink,
    adId,
    proofIndex
  ) => {
    const uniqueKey = `${adId}-${proofIndex}`;

    try {
      if (!platform || !videoLink) {
        toast.error("Platform or video link is missing");
        return;
      }

      // Set loading state for this specific button
      setCheckingViews((prev) => ({ ...prev, [uniqueKey]: true }));

      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/check/current/view`,
        {
          platform: platform,
          videoUrl: videoLink,
        },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        }
      );

      console.log("Current view response:", response.data);

      if (response.data.success) {
        // Store the views for this specific proof
        setAdViews((prev) => ({ ...prev, [uniqueKey]: response.data.views }));
      } else {
        toast.error("Failed to fetch current views");
      }
    } catch (error) {
      console.error("Error checking current view:", error);
      toast.error("Failed to check current views. Please try again.");
    } finally {
      // Remove loading state
      setCheckingViews((prev) => ({ ...prev, [uniqueKey]: false }));
    }
  };

  // Calculate pagination data for main advertisements list
  // Sort advertisements by latest date first (most recent at top)
  const sortedAdvertisements = [...advertisements].sort((a, b) => {
    // If ads have proofs, sort by the latest proof submission date
    const getLatestProofDate = (ad) => {
      if (!ad.proofs || ad.proofs.length === 0) return new Date(0);
      const dates = ad.proofs.map(proof => new Date(proof.submittedAt || 0));
      return Math.max(...dates);
    };

    const dateA = getLatestProofDate(a);
    const dateB = getLatestProofDate(b);
    return dateB - dateA; // Most recent first
  });

  // Search filter
  const filteredAds = sortedAdvertisements.filter(ad => {
    if (!searchTerm) return true;
    const searchStr = searchTerm.toLowerCase();
    return ad._id?.toLowerCase().includes(searchStr) ||
      ad.adId?.toLowerCase().includes(searchStr) ||
      ad.id?.toLowerCase().includes(searchStr);
  });

  // Pagination for main
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  const totalPages = Math.ceil(filteredAds.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedAdvertisements = filteredAds.slice(startIndex, endIndex);

  // Handle page change for main advertisements list
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Helper function to format date in dd/mm/yy hh:mm:ss format
  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "N/A";

    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = String(date.getFullYear()).slice(-2);
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
  };

  return (
    <div className="p-2 sm:p-4 md:p-6">
      {/* Screenshot Modal */}
      {showScreenshotModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm transition-all">
          <div className="relative bg-gradient-to-br from-white via-gray-50 to-gray-200 rounded-2xl shadow-2xl p-0 max-w-2xl w-full border border-gray-200 animate-fadeIn">
            <button
              className="absolute top-2 right-2 sm:top-4 sm:right-4 text-gray-400 hover:text-red-500 text-2xl sm:text-3xl font-bold focus:outline-none focus:ring-2 focus:ring-red-300 transition"
              onClick={() => setShowScreenshotModal(false)}
              aria-label="Close"
            >
              &times;
            </button>
            <div className="flex flex-col items-center justify-center p-4 sm:p-8">
              <h2 className="text-lg sm:text-2xl font-bold mb-2 sm:mb-4 text-gray-800 flex items-center gap-2">
                <FaEye className="text-blue-500" /> Screenshot Preview
              </h2>
              <div className="w-full flex items-center justify-center min-h-[200px] sm:min-h-[300px] bg-gray-100 rounded-xl border border-dashed border-gray-300 shadow-inner overflow-hidden">
                <img
                  src={currentScreenshot}
                  alt="Screenshot"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "/fallback-image.jpg";
                  }}
                  className="max-h-[50vh] sm:max-h-[60vh] max-w-full rounded-xl shadow-lg border border-gray-200 object-contain transition-transform duration-300 hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      <ToastContainer position="top-right" autoClose={3000} />

      <div className="flex flex-col mb-6 gap-4 mt-2">
        <h1 className="text-xl sm:text-2xl font-black text-slate-800 tracking-tight">
          Running Advertisements
        </h1>
        {/* Floating Pill Bar for Desktop & Mobile Navigation */}
        <div className="bg-white/90 backdrop-blur-xl shadow-lg shadow-slate-200/40 border border-slate-100 rounded-[1.5rem] sm:rounded-full p-1.5 sm:p-2 w-full mx-auto pointer-events-auto transition-all">
          <div className="flex flex-wrap justify-center sm:justify-start items-center gap-1.5 sm:gap-2 w-full">
            {[
              { path: '/advertiser/leads-status/underReview', label: 'Under Review', activeColor: 'bg-yellow-500', activeShadow: 'shadow-yellow-500/30' },
              { path: '/advertiser/leads-status/pending', label: 'Approved', activeColor: 'bg-blue-500', activeShadow: 'shadow-blue-500/30' },
              { path: '/advertiser/leads-status/accepted', label: 'Accepted', activeColor: 'bg-teal-500', activeShadow: 'shadow-teal-500/30' },
              { path: '/advertiser/leads-status/running', label: 'Running', activeColor: 'bg-emerald-500', activeShadow: 'shadow-emerald-500/30', active: true },
              { path: '/advertiser/leads-status/completed', label: 'Completed', activeColor: 'bg-indigo-500', activeShadow: 'shadow-indigo-500/30' },
              { path: '/advertiser/leads-status/rejected', label: 'Rejected', activeColor: 'bg-rose-500', activeShadow: 'shadow-rose-500/30' }
            ].map((item, idx) => (
              <Link
                key={idx}
                to={item.path}
                className={`
                  flex-1 sm:flex-none flex items-center justify-center px-2 sm:px-4 py-2 sm:py-2.5 
                  rounded-xl sm:rounded-full transition-all duration-300 text-[10px] sm:text-xs font-black uppercase tracking-widest text-center whitespace-nowrap
                  ${item.active
                    ? `${item.activeColor} text-white shadow-md ${item.activeShadow} scale-[1.02]`
                    : 'bg-transparent text-slate-500 hover:bg-slate-50 hover:text-slate-700 active:scale-95'
                  }
                `}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Stats Card */}
      <div className="mb-4 sm:mb-8">
        <div className="bg-green-500 p-3 sm:p-4 md:p-6 rounded-lg sm:rounded-xl shadow-lg text-white">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs sm:text-sm opacity-80">Running Advertisements</p>
              <p className="text-xl sm:text-2xl font-bold">{advertisements.length}</p>

            </div>
            <FaPlay className="w-6 h-6 sm:w-8 sm:h-8 md:w-10 md:h-10 opacity-80" />
          </div>
        </div>
      </div>

      {/* Advertisement Cards */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 md:h-12 md:w-12 border-t-2 border-b-2 border-green-500"></div>
        </div>
      ) : advertisements.length === 0 ? (
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm p-4 sm:p-6 md:p-8 text-center">
          <FaPlay className="w-10 h-10 sm:w-12 sm:h-12 md:w-16 md:h-16 mx-auto text-gray-300 mb-2 sm:mb-3 md:mb-4" />
          <h3 className="text-base sm:text-lg md:text-xl font-semibold text-gray-700">
            No Running Advertisements
          </h3>
          <p className="text-xs sm:text-sm md:text-base text-gray-500">
            You don't have any running advertisements at the moment.
          </p>
        </div>
      ) : (
        <>
          {/* Search Bar */}
          <div className="mb-4 sm:mb-6">
            <div className="relative max-w-2xl mx-auto">
              <input
                type="text"
                placeholder="Search by Advertisement ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-11 pr-4 py-3 sm:py-3.5 rounded-2xl border-2 border-slate-100 bg-white focus:outline-none focus:ring-4 focus:ring-yellow-500/10 focus:border-yellow-500 transition-all text-sm sm:text-base shadow-sm font-medium text-slate-700 placeholder:text-slate-400"
              />
              <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-1 gap-3 sm:gap-4 md:gap-6">
            {paginatedAdvertisements.map((ad) => {
              if (ad.proofs && ad.proofs.length > 0) {
                // Sort proofs by submission date (most recent first)
                const sortedProofs = [...ad.proofs].sort((a, b) => {
                  const dateA = new Date(a.submittedAt || 0);
                  const dateB = new Date(b.submittedAt || 0);
                  return dateB - dateA; // Most recent first
                });

                // Pagination logic for proofs (per ad)
                const totalPages = Math.ceil(sortedProofs.length / rowsPerPage);
                const currentPage = adPageMap[ad.adId] || 1;
                const startIdx = (currentPage - 1) * rowsPerPage;
                const endIdx = startIdx + rowsPerPage;
                const paginatedProofs = sortedProofs.slice(startIdx, endIdx);
                const handleAdPageChange = (adId, page) => {
                  setAdPageMap((prev) => ({ ...prev, [adId]: page }));
                };
                return (
                  <div key={ad.adId} className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    {/* Header with Ad Details */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 sm:p-3 md:p-4">
                      <div className="flex justify-between items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <h3 className="text-xs sm:text-sm md:text-lg font-bold truncate">
                              🆔 {ad.adId} • {ad.adType || "N/A"}
                            </h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(ad.adId);
                                toast.success('ID Copied!');
                              }}
                              className="bg-white/20 hover:bg-white/30 text-white rounded p-1 transition-colors flex-shrink-0"
                              title="Copy ID"
                            >
                              <FaCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                          <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1 truncate">
                            {ad.mediaType?.toUpperCase()} • {ad.userType?.toUpperCase()} • {ad.adLength}s
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-base sm:text-xl md:text-2xl font-bold">{ad.requiredViews?.toLocaleString() || "N/A"}</div>
                          <div className="text-blue-100 text-xs sm:text-sm">Required Views</div>
                        </div>
                      </div>
                    </div>

                    {/* Ad Details Grid */}
                    <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 bg-gray-50 border-b">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium">TOTAL COST</div>
                          <div className="text-xs sm:text-sm font-bold text-gray-900">₹{ad.totalCost ? Number(ad.totalCost).toFixed(2) : "N/A"}</div>
                        </div>
                        {ad.startDate && ad.endDate && (
                          <div>
                            <div className="text-[10px] sm:text-xs text-gray-500 font-medium">CAMPAIGN</div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                              {ad.startDate} to {ad.endDate}
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium tracking-wider">TARGET LOCATION</div>
                          <div className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-900 truncate" title={ad.pfCities?.join(', ')}>
                            {ad.pfState ? `${ad.pfState}${ad.pfCities && ad.pfCities.length > 0 ? ` (${ad.pfCities.length} cities)` : ''}` : "N/A"}
                          </div>
                          {(ad.adminSelectState || (ad.adminSelectCities && ad.adminSelectCities.length > 0)) && (
                            <div className="mt-1 pt-0.5 border-t border-gray-200">
                              <div className="text-[8px] sm:text-[9px] font-black text-blue-500 uppercase tracking-widest leading-none mb-0.5">Admin Modified</div>
                              <div className="text-[10px] sm:text-xs font-bold text-blue-700 truncate" title={ad.adminSelectCities?.join(', ')}>
                                {ad.adminSelectState || "Multi-State"}{ad.adminSelectCities && ad.adminSelectCities.length > 0 ? ` (${ad.adminSelectCities.length} cities)` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium">SUBMISSIONS</div>
                          <div className="text-xs sm:text-sm font-bold text-blue-600">{ad.totalProofs || 0}</div>
                        </div>
                      </div>
                    </div>

                    {/* Media Description */}
                    {ad.mediaDescription && (
                      <div className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-blue-50 border-b">
                        <div className="text-[10px] sm:text-xs text-blue-600 font-medium mb-0.5 sm:mb-1">DESCRIPTION</div>
                        <div className="text-xs sm:text-sm text-gray-900">{ad.mediaDescription}</div>
                      </div>
                    )}

                    <div className="px-1 py-1">{/* Table container */}
                      {/* Table with paginated proofs */}
                      <div className="hidden lg:block overflow-x-auto -mt-4">
                        <table className="min-w-full rounded-lg mt-0 text-xs sm:text-sm">
                          <thead className="bg-gray-50 border border-gray-200">
                            <tr>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Platform</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Channel Name</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Video</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Ads Timing</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Current Views</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Submitted Date</th>
                              <th className="px-1.5 sm:px-2 md:px-4 py-1.5 sm:py-2 md:py-3 text-left text-[10px] sm:text-xs font-medium text-gray-500 uppercase tracking-wider border-b">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedProofs.map((proof, proofIndex) => {
                              // proofIndex here is relative to paginatedProofs, need global index for keys
                              const globalIndex = startIdx + proofIndex;
                              return (
                                <tr key={globalIndex} className="bg-white border-b border-gray-100 hover:bg-gray-50/50">
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2"><div className="text-xs sm:text-sm font-black text-gray-900 truncate tracking-tight">{proof.platform || "N/A"}</div></td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2"><div className="text-xs sm:text-sm font-medium text-gray-900 truncate">{proof.channelName || "N/A"}</div></td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2"><div className="text-xs sm:text-sm text-gray-900">{proof.videoLink ? (<a href={proof.videoLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-0.5 sm:gap-1 px-2.5 md:px-3 py-1 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-lg text-xs hover:bg-indigo-100 w-fit font-bold shadow-sm transition-all"><FaPlay className="w-2.5 h-2.5" /> <span className="hidden sm:inline">Watch Video</span><span className="sm:hidden">Watch</span></a>) : ("N/A")}</div></td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2"><div className="text-[10px] sm:text-xs font-bold text-gray-600 bg-slate-100 px-2 py-1 rounded w-fit">{proof.duration || "N/A"}</div></td>
                                  <td className="px-1 sm:px-2 py-2 whitespace-nowrap">
                                    {proof.platform?.toLowerCase() === 'instagram' ? (
                                      <span className="text-red-600 font-semibold text-xs flex items-center gap-1">⚠️ Check Manually</span>
                                    ) : (
                                      <button
                                        onClick={() => handleCheckCurrentView(proof.platform, proof.videoLink, ad.adId, globalIndex)}
                                        disabled={checkingViews[`${ad.adId}-${globalIndex}`]}
                                        className="px-2 md:px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] sm:text-xs font-bold rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors flex items-center shadow-sm"
                                      >
                                        {checkingViews[`${ad.adId}-${globalIndex}`] ? (
                                          <><FaSpinner className="animate-spin mr-1 sm:mr-2 h-3 w-3" /><span className="hidden sm:inline">Checking...</span><span className="sm:hidden">...</span></>
                                        ) : adViews[`${ad.adId}-${globalIndex}`] ? (
                                          <span className="truncate flex items-center gap-1"><FaEye className="w-3 h-3" /> {adViews[`${ad.adId}-${globalIndex}`]} <span className="hidden sm:inline">views</span></span>
                                        ) : (
                                          <>
                                            <FaEye className="w-3 h-3 mr-1" />
                                            <span className="hidden sm:inline">Check Views</span>
                                            <span className="sm:hidden">Check</span>
                                          </>
                                        )}
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-1.5 sm:px-2 md:px-4 py-2"><div className="text-[10px] sm:text-xs font-mono text-gray-500 font-bold bg-slate-50 px-2 py-1 rounded border border-slate-100 w-fit">{formatDateTime(proof.submittedAt)}</div></td>
                                  <td className="px-1 sm:px-2 md:px-3 py-2">
                                    <button
                                      className="px-2.5 md:px-3 py-1.5 bg-slate-800 text-white rounded-lg text-[10px] sm:text-xs font-bold hover:bg-slate-700 shadow-sm flex items-center gap-1"
                                      onClick={() => {
                                        if (!proof.screenshot) {
                                          console.warn("No screenshot found for this proof.");
                                          return;
                                        }
                                        const normalizedPath = proof.screenshot.replace(/\\/g, "/");
                                        const fullImageUrl = `${process.env.REACT_APP_API_BASE_URL}/${normalizedPath}`;
                                        setCurrentScreenshot(fullImageUrl);
                                        setShowScreenshotModal(true);
                                      }}
                                    >
                                      <FaImage className="w-3 h-3" />
                                      <span className="hidden sm:inline">Screenshot</span><span className="sm:hidden">View</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>

                      {/* Mobile Card View for Proofs (Hidden on LG and up) */}
                      <div className="lg:hidden flex flex-col gap-3 mt-2 px-1">
                        {paginatedProofs.map((proof, proofIndex) => {
                          const globalIndex = startIdx + proofIndex;
                          return (
                            <div key={globalIndex} className="bg-white rounded-xl shadow-sm border border-slate-100 p-3 flex flex-col gap-3 relative">
                              {/* Header: Platform & Time */}
                              <div className="flex justify-between items-start border-b border-slate-50 pb-2">
                                <div>
                                  <p className="text-xs font-black text-slate-800 uppercase tracking-widest">{proof.platform || "N/A"}</p>
                                  <p className="text-[10px] font-bold text-slate-500 mt-0.5 max-w-[150px] truncate">{proof.channelName || "N/A"}</p>
                                </div>
                                <span className="text-[9px] font-bold text-slate-400 font-mono bg-slate-50 px-2 py-0.5 rounded border border-slate-100">
                                  {formatDateTime(proof.submittedAt)}
                                </span>
                              </div>

                              {/* Body: Actions & Data */}
                              <div className="flex flex-wrap items-center justify-between gap-2">

                                {/* Left side actions */}
                                <div className="flex flex-col gap-1.5">
                                  {proof.videoLink && (
                                    <a href={proof.videoLink} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-50 text-indigo-600 border border-indigo-100 rounded-lg text-xs hover:bg-indigo-100 w-fit font-bold shadow-sm active:scale-95 transition-all">
                                      <FaPlay className="w-2.5 h-2.5" /> Watch Video
                                    </a>
                                  )}
                                  <button
                                    className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white rounded-lg text-xs font-bold hover:bg-slate-700 shadow-sm active:scale-95 transition-all w-fit"
                                    onClick={() => {
                                      if (!proof.screenshot) return;
                                      const fullImageUrl = `${process.env.REACT_APP_API_BASE_URL}/${proof.screenshot.replace(/\\/g, "/")}`;
                                      setCurrentScreenshot(fullImageUrl);
                                      setShowScreenshotModal(true);
                                    }}
                                  >
                                    <FaImage className="w-3 h-3" /> Screenshot
                                  </button>
                                </div>

                                {/* Right side data */}
                                <div className="flex flex-col items-end gap-1.5">
                                  <div className="text-[10px] font-bold text-gray-600 bg-slate-100 px-2.5 py-1 rounded w-fit flex items-center gap-1">
                                    <FaHourglassHalf className="w-2.5 h-2.5 text-slate-400" /> {proof.duration || "N/A"}
                                  </div>

                                  {proof.platform?.toLowerCase() === 'instagram' ? (
                                    <span className="text-red-600 font-semibold text-xs flex items-center gap-1 mt-1">⚠️ Check Manually</span>
                                  ) : (
                                    <button
                                      onClick={() => handleCheckCurrentView(proof.platform, proof.videoLink, ad.adId, globalIndex)}
                                      disabled={checkingViews[`${ad.adId}-${globalIndex}`]}
                                      className="px-3 py-1.5 bg-emerald-50 text-emerald-600 border border-emerald-100 text-[10px] sm:text-xs font-bold rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors flex items-center shadow-sm active:scale-95"
                                    >
                                      {checkingViews[`${ad.adId}-${globalIndex}`] ? (
                                        <><FaSpinner className="animate-spin mr-1.5 h-3 w-3" /><span>Checking...</span></>
                                      ) : adViews[`${ad.adId}-${globalIndex}`] ? (
                                        <span className="flex items-center gap-1"><FaEye className="w-3 h-3" /> {adViews[`${ad.adId}-${globalIndex}`]} views</span>
                                      ) : (
                                        <><FaEye className="w-3 h-3 mr-1.5" /><span>Check Views</span></>
                                      )}
                                    </button>
                                  )}

                                </div>

                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="w-full flex justify-center items-center mt-3 sm:mt-6 z-10">
                          <nav className="flex flex-row flex-wrap justify-center items-center gap-1 sm:gap-2 w-auto max-w-full px-1 sm:px-2 py-0.5 sm:py-1 bg-white rounded-lg shadow-md border border-yellow-200 sticky bottom-0 sm:static">
                            <button
                              onClick={() => handleAdPageChange(ad.adId, Math.max(currentPage - 1, 1))}
                              disabled={currentPage === 1}
                              className={`px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                              aria-label="Previous Page"
                            >
                              Prev
                            </button>
                            <div className="flex flex-row flex-wrap gap-0.5 sm:gap-1">
                              {[...Array(totalPages)].map((_, idx) => (
                                <button
                                  key={idx + 1}
                                  onClick={() => handleAdPageChange(ad.adId, idx + 1)}
                                  className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded border border-yellow-400 text-xs sm:text-sm ${currentPage === idx + 1 ? 'bg-yellow-300 text-yellow-900 font-bold' : 'bg-white text-yellow-800 hover:bg-yellow-100'} transition-colors`}
                                  aria-label={`Go to page ${idx + 1}`}
                                >
                                  {idx + 1}
                                </button>
                              ))}
                            </div>
                            <button
                              onClick={() => handleAdPageChange(ad.adId, Math.min(currentPage + 1, totalPages))}
                              disabled={currentPage === totalPages}
                              className={`px-2 sm:px-3 md:px-4 py-0.5 sm:py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                              aria-label="Next Page"
                            >
                              Next
                            </button>
                          </nav>
                        </div>
                      )}
                    </div>
                  </div>
                );
              } else {
                return (
                  <div key={ad.adId} className="bg-white rounded-lg sm:rounded-xl shadow-lg border border-gray-200 overflow-hidden">
                    {/* Header with Ad Details */}
                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-2 sm:p-3 md:p-4">
                      <div className="flex justify-between items-center gap-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 overflow-hidden">
                            <h3 className="text-xs sm:text-sm md:text-lg font-bold truncate">
                              🆔 {ad.adId} • {ad.adType || "N/A"}
                            </h3>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigator.clipboard.writeText(ad.adId);
                                toast.success('ID Copied!');
                              }}
                              className="bg-white/20 hover:bg-white/30 text-white rounded p-1 transition-colors flex-shrink-0"
                              title="Copy ID"
                            >
                              <FaCopy className="w-3 h-3 sm:w-4 sm:h-4" />
                            </button>
                          </div>
                          <p className="text-blue-100 text-xs sm:text-sm mt-0.5 sm:mt-1 truncate">
                            {ad.mediaType?.toUpperCase()} • {ad.userType?.toUpperCase()} • {ad.adLength}s
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-base sm:text-xl md:text-2xl font-bold">{ad.requiredViews?.toLocaleString() || "N/A"}</div>
                          <div className="text-blue-100 text-xs sm:text-sm">Required Views</div>
                        </div>
                      </div>
                    </div>

                    {/* Ad Details Grid */}
                    <div className="px-2 sm:px-3 md:px-4 py-2 sm:py-3 bg-gray-50 border-b">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium">TOTAL COST</div>
                          <div className="text-xs sm:text-sm font-bold text-gray-900">₹{ad.totalCost ? Number(ad.totalCost).toFixed(2) : "N/A"}</div>
                        </div>
                        {ad.startDate && ad.endDate && (
                          <div>
                            <div className="text-[10px] sm:text-xs text-gray-500 font-medium">CAMPAIGN</div>
                            <div className="text-xs sm:text-sm font-semibold text-gray-900 truncate">
                              {ad.startDate} to {ad.endDate}
                            </div>
                          </div>
                        )}
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium tracking-wider">TARGET LOCATION</div>
                          <div className="text-[10px] sm:text-xs md:text-sm font-semibold text-gray-900 truncate" title={ad.pfCities?.join(', ')}>
                            {ad.pfState ? `${ad.pfState}${ad.pfCities && ad.pfCities.length > 0 ? ` (${ad.pfCities.length} cities)` : ''}` : "N/A"}
                          </div>
                          {(ad.adminSelectState || (ad.adminSelectCities && ad.adminSelectCities.length > 0)) && (
                            <div className="mt-1 pt-0.5 border-t border-gray-200">
                              <div className="text-[8px] sm:text-[9px] font-black text-blue-500 uppercase tracking-widest leading-none mb-0.5">Admin Modified</div>
                              <div className="text-[10px] sm:text-xs font-bold text-blue-700 truncate" title={ad.adminSelectCities?.join(', ')}>
                                {ad.adminSelectState || "Multi-State"}{ad.adminSelectCities && ad.adminSelectCities.length > 0 ? ` (${ad.adminSelectCities.length} cities)` : ''}
                              </div>
                            </div>
                          )}
                        </div>
                        <div>
                          <div className="text-[10px] sm:text-xs text-gray-500 font-medium">SUBMISSIONS</div>
                          <div className="text-xs sm:text-sm font-bold text-blue-600">0</div>
                        </div>
                      </div>
                    </div>

                    {/* Media Description */}
                    {ad.mediaDescription && (
                      <div className="px-2 sm:px-3 md:px-4 py-1.5 sm:py-2 bg-blue-50 border-b">
                        <div className="text-[10px] sm:text-xs text-blue-600 font-medium mb-0.5 sm:mb-1">DESCRIPTION</div>
                        <div className="text-xs sm:text-sm text-gray-900">{ad.mediaDescription}</div>
                      </div>
                    )}

                    {/* No Proofs Message */}
                    <div className="p-3 sm:p-4 md:p-6 text-center bg-gray-50">
                      <FaPlay className="w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 mx-auto text-gray-300 mb-2 sm:mb-3" />
                      <p className="text-xs sm:text-sm md:text-base text-gray-500 font-medium">No reporter submissions yet</p>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-400">Waiting for reporters to submit their work</p>
                      <p className="text-xs sm:text-sm md:text-base text-gray-500 font-medium">
                        No reporter submissions yet
                      </p>
                      <p className="text-[10px] sm:text-xs md:text-sm text-gray-400">
                        Waiting for reporters to submit their work
                      </p>
                    </div>
                  </div>
                );
              }
            })}
          </div>
        </>
      )}

      {/* Main Advertisements Pagination Controls */}
      {!loading && filteredAds.length > 0 && totalPages > 1 && (
        <div className="flex justify-center mt-3 sm:mt-6 px-2 sm:px-4">
          <nav className="inline-flex items-center space-x-1 sm:space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm md:text-base ${currentPage === 1 ? "opacity-50 cursor-not-allowed" : ""
                }`}
            >
              Prev
            </button>
            <span className="px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 bg-yellow-300 text-yellow-900 font-bold text-xs sm:text-sm md:text-base">
              {currentPage}
            </span>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className={`px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors text-xs sm:text-sm md:text-base ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              Next
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default RunningAdvertisements;
